import React, { Fragment, useEffect, useState, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import GigyaService from "../../services/gigya-service";
import {
  CoreButton,
  CoreNotification,
  CoreTextfield,
} from "@komodo-design-system/komodo-components-react";
import "./Signin.scss";
import CookieService from "../../services/cookie.service";
import { updatedNotificationLink } from "../../actions/updatedNotificationLink";
import config from "../../constants/config";
import CommonSigninService from "../../services/common-signin.service";
import { configureDigitalDataOnError } from "../../utils/adobeAnalytics.utils";
import useRedirectUrl from "../../hooks/rpDetails";
import {
  getSAMLContext,
  useNoSessionGuard,
} from "../SigninControllers/signin-controllers";
import {
  getStoredRpUrls,
  processServiceUrls,
} from "../../utils/serviceUrls.utils";
import { getCountry } from "../../services/localeSupport.service";
import backArrow from "../../assets/images/backarrow.png";

const getBackUrl = () => {
  const referrer = document.referrer;
  const currentPath = window.location.pathname;

  if (
    referrer.includes("mfa-signin-identifier") ||
    currentPath.includes("mfa-signin")
  ) {
    return "/account-center/mfa-signin-identifier.html";
  }

  if (
    referrer.includes("dcr-signin-identifier") ||
    currentPath.includes("dcr-signin")
  ) {
    return "/account-center/dcr-signin-identifier.html";
  }

  if (
    referrer.includes("cloud-signin-identifier") ||
    currentPath.includes("cloud-signin")
  ) {
    return "/account-center/cloud-signin-identifier.html";
  }

  if (
    referrer.includes("simplified-cloud-username") ||
    currentPath.includes("simplified-cloud-password")
  ) {
    return "/account-center/simplified-cloud-username.html";
  }

  if (
    referrer.includes("simplified-username") ||
    CookieService.getCookie("cip_ru") === "true"
  ) {
    return "/account-center/simplified-username.html";
  }

  return "/account-center/signin-identifier.html";
};

const Signin = () => {
  const isCloudSigninFlow = window.location.href.includes("cloud");
  useNoSessionGuard(!isCloudSigninFlow);
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const rpdetailsUrl = useRedirectUrl();
  const hideCommerceSidePanel = rpdetailsUrl?.hideCommerceSidePanel === true;
  const passwordInputRef = useRef(null);
  const signinButtonRef = useRef(null);
  const handleLoginRef = useRef(null);
  const [areStencilControlsReady, setAreStencilControlsReady] =
    useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [pwdErrorMessage, setPwdErrorMessage] = useState(null);
  const [isTouched, setIsTouched] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorToast, setErrorToast] = useState(false);
  const [createAccountUrl, setCreateAccountUrl] = useState(
    "/global-registration/registration",
  );
  const [resetPwdUrl, setResetPwdUrl] = useState(
    "/identity/account/reset-password/v2/request",
  );
  const country = (getCountry() || "").toLowerCase();
  const isNotChinaDomain =
    country === "cn" &&
    rpdetailsUrl?.logoLink?.indexOf(".thermofisher.com.cn") !== -1;
  // Get SSI content from context

  // Get cip_ru cookie value
  const cipRuCookie = CookieService.getCookie("cip_ru");
  const cipRu = cipRuCookie === "true" || cipRuCookie === "email";
  const mfaApiKey = CommonSigninService.getEnvVar("REACT_APP_MFA_CDC_API_KEY");
  const isMfaLoginFlow =
    window.location.pathname.toLowerCase().includes("mfa") ||
    sessionStorage.getItem("gig_apikey") === mfaApiKey;

  const redirectInSameTab = (url) => {
    CommonSigninService.clearSigninSessionStorage();
    CommonSigninService.clearIdentifierCookie();
    window.location.assign(url);
  };

  const sendUpdatedNotification = async (uid, storedValue) => {
    if (
      storedValue &&
      (storedValue.includes("dev2.apps.thermofisher") ||
        storedValue.includes("stage.apps.thermofisher") ||
        storedValue.includes("test.apps.thermofisher"))
    ) {
      try {
        const response = await dispatch(updatedNotificationLink(uid));

        if (response?.successMessage) {
        } else {
          console.error(
            "Error:",
            response?.error || "Failed to send notification",
          );
        }
      } catch (error) {
        console.error("Error:", error.message);
      }
    } else {
      console.error("Stored value does not match the required conditions.");
    }
  };

  const handleLogin = async (e) => {
    // e.preventDefault();
    setIsTouched(true);

    // Validate password using common service
    const validation = CommonSigninService.validatePassword(password);
    if (!validation.isValid) {
      setPwdErrorMessage("errors.EMPTY_PASSWORD");
      configureDigitalDataOnError(validation.error);
      return;
    }

    const params = CommonSigninService.createLoginParams(
      username.trim(),
      password,
    );

    setIsLoading(true);
    try {
      const loginApiKey = isMfaLoginFlow
        ? mfaApiKey
        : CommonSigninService.getEnvVar("REACT_APP_CDC_API_KEY");

      if (isMfaLoginFlow && mfaApiKey) {
        CommonSigninService.setApiKey(mfaApiKey);
        GigyaService.setUpMfaCdcScript();
      }

      const response = await GigyaService.accountLogin(params, loginApiKey);

      if (response.errorCode === 0) {
        // Keep loader visible - page will redirect
        CommonSigninService.handleLoginSuccess(response, (uid, storedValue) =>
          sendUpdatedNotification(uid, storedValue),
        );
        return; // Don't set isLoading to false, redirect will happen
      }

      // Handle login errors using common service
      const errorResult = CommonSigninService.handleLoginError(response);

      switch (errorResult.action) {
        case "redirect":
          // Keep loader visible - page will redirect
          window.location.href = errorResult.url;
          break;
        case "redirectToTFA":
          try {
            const tfaParams = {
              regToken:
                errorResult.regToken ||
                sessionStorage.getItem("prt") ||
                sessionStorage.getItem("ert"),
              provider: errorResult.provider,
              mode: errorResult.mode,
            };
            const tfaResponse = await GigyaService.initTFA(tfaParams);
            CommonSigninService.setGigyaAssertion(tfaResponse?.gigyaAssertion);
            if (errorResult.mode === "verify") {
              CommonSigninService.navigateToMFAEmailVerify();
            } else {
              CommonSigninService.navigateToMFAVerification(
                CommonSigninService.getEnvVar("REACT_APP_MFA_CDC_API_KEY"),
              );
            }
            // Keep loader visible - page will redirect
          } catch (tfaError) {
            CommonSigninService.logError("TFA initialization failed", tfaError);
            setErrorToast(true);
            setIsLoading(false);
          }
          break;
        case "show_error":
          setErrorToast(true);
          setIsLoading(false);
          break;
        case "finalize_registration": {
          // Keep loader visible - page will redirect after finalize
          const resp = await GigyaService.finalizeRegistration(
            errorResult.regToken,
          );
          CommonSigninService.handleAuthRedirection(resp);
          break;
        }
        case "unknown_error":
        default:
          CommonSigninService.logError("Unknown error occurred", errorResult);
          setErrorToast(true);
          setIsLoading(false);
          break;
      }
    } catch (error) {
      CommonSigninService.logError("Error in accountLogin", error);
      setErrorToast(true);
      setIsLoading(false);
    }
  };

  // Always keep ref in sync with latest handleLogin
  handleLoginRef.current = handleLogin;

  // Get username from session storage on component mount
  useEffect(() => {
    const backUrl = getBackUrl();
    const isFromIdentifier =
      location.state?.fromIdentifier ||
      sessionStorage.getItem("signin_identifier_redirect") === "true";

    if (window.location.href.includes("cloud")) {
      getSAMLContext();
    }

    // If page is loaded directly (no referrer), redirect to signin-identifier
    if (
      !isFromIdentifier &&
      (!document.referrer || document.referrer === "")
    ) {
      window.location.href = backUrl;
      return;
    }

    const cookieValue = CookieService.getCookie("cip_identifier");
    if (cookieValue) {
      setUsername(cookieValue);
    } else {
      // If no username stored, redirect back to identifier page
      navigate(backUrl, { replace: true });
      return;
    }

    let cleanupKeyDown = null;

    // Poll until shadow DOM is ready (retries every 100ms, up to 2s)
    let attempts = 0;
    const maxAttempts = 20;
    const interval = setInterval(() => {
      attempts++;
      const input = passwordInputRef.current?.shadowRoot?.querySelector(
        ".core-textfield__input",
      );
      const button =
        signinButtonRef.current?.shadowRoot?.querySelector(".core-button");

      if (input) {
        input.id = "password-field";

        // Attach keydown listener for Enter key
        const handleKeyDown = (e) => {
          if (e.key === "Enter") {
            handleLoginRef.current();
          }
        };
        input.addEventListener("keydown", handleKeyDown);
        cleanupKeyDown = () =>
          input.removeEventListener("keydown", handleKeyDown);
      }

      if (button) {
        button.id = "signin-button";
      }

      if (input && button) {
        setAreStencilControlsReady(true);
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        setAreStencilControlsReady(true);
        clearInterval(interval);
      }
    }, 100);

    // Load service URLs from session storage
    const storedRpUrls = getStoredRpUrls();
    if (storedRpUrls) {
      const result = processServiceUrls(storedRpUrls);
      setCreateAccountUrl(result.createAccountUrl);
      setResetPwdUrl(result.resetPwdUrl);
    }

    return () => {
      clearInterval(interval);
      if (cleanupKeyDown) cleanupKeyDown();
    };
  }, []);
  useEffect(() => {
    if (rpdetailsUrl && typeof rpdetailsUrl === "object") {
      // Update header logo link
      window.tfIdentityGlobals?.setHeaderLogLink?.(
        rpdetailsUrl?.logoLink,
        true,
      );
      window.tfIdentityGlobals.setFooterLinks({
        privacy: rpdetailsUrl?.privacy_policy,
      });
      window?.tfIdentityGlobals?.hideLoader();
    }
  }, [rpdetailsUrl]);

  const isReady =
    rpdetailsUrl &&
    typeof rpdetailsUrl === "object" &&
    areStencilControlsReady;

  useEffect(() => {
    if (!isReady) return undefined;

    const focusTimer = setTimeout(() => {
      passwordInputRef.current?.shadowRoot
        ?.querySelector(".core-textfield__input")
        ?.focus();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, [isReady]);

  return (
    <Fragment>
      <div className={`loginForm__main signin-page${isReady ? " signin-page--ready" : ""}`}>
        <div
          className={`loginForm ${cipRu || hideCommerceSidePanel || isNotChinaDomain ? "cipRu-specific-class" : ""}`}
        >
          <div className="loginForm__left add_loader_class">
            {isLoading && (
              <div className="loader-overlay">
                <div className="spinner">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className="spinner-blade" />
                  ))}
                </div>
              </div>
            )}
            <p className="loginForm__left__signin-title">
              {t("translations.SIGN_INTO_LABEL")}
            </p>
            {errorToast && (
              <CoreNotification
                className="notification"
                label={t("serviceError.errorLabel")}
                message={t("loginScreenErrors.invalid_login_or_password")}
                variant="toast"
                state="error"
                nocloseicon={true}
              />
            )}
            <div className="loginForm__left__form">
              <div className="loginForm__left__form__user with-back-link">
                <a
                  href={getBackUrl()}
                  className="back-link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(getBackUrl(), { state: { fromSignin: true } });
                  }}
                >
                  <img
                    src={backArrow}
                    alt="Back"
                    className="icon-16-arrow-left-bold back-icon"
                  />
                  {/* <span className="icon-16-arrow-left-bold"></span> */}
                </a>
                <span className="loginForm__left__form__user__name">
                  {username}
                </span>
              </div>
                <CoreTextfield
                  ref={passwordInputRef}
                  type="password"
                  tooltip-focusable
                  className="loginForm__left__password"
                  data-testid="password-field"
                  label={t("translations.PASSWORD_132128826476804690_LABEL")}
                  required={country === "cn"}
                  fullWidth
                  name="password"
                  toggleText={{ show: t("loginMessages.show_password"), hide: t("loginMessages.hide_password"), }}
                  autocomplete={true}
                  value={password}
                  invalid={isTouched && !password && !!pwdErrorMessage}
                  helper={
                    isTouched && !password && pwdErrorMessage
                      ? t(pwdErrorMessage)
                      : undefined
                  }
                  onTextfieldChange={(e) => {
                    const value = e.target.value.replace(/\s/g, "");
                    setPassword(value);
                    setIsDirty(true);
                    if (isTouched) {
                      if (!value.trim()) {
                        setPwdErrorMessage("errors.EMPTY_PASSWORD");
                      } else {
                        setPwdErrorMessage("");
                      }
                    }
                  }}
                  onTextfieldFocusOut={() => {
                    setIsTouched(true);
                    if (isDirty && !password.trim()) {
                      setPwdErrorMessage("errors.EMPTY_PASSWORD");
                    }
                  }}
                  onBlur={() => {
                    setIsTouched(true);
                    if (isDirty && !password.trim()) {
                      setPwdErrorMessage("errors.EMPTY_PASSWORD");
                    }
                  }}
                />
              <div>
                <CoreButton
                  ref={signinButtonRef}
                  type="button"
                  variant="primary"
                  size="large"
                  fullspace
                  onClick={handleLogin}
                >
                  <span slot="content">
                    {t("translations.SUBMIT_142674579108303380_VALUE")}
                  </span>
                </CoreButton>
                <p className="loginForm__left__form__tip_tool">
                  <a
                    className="loginForm__left__form__tip_tool__trouble-signin"
                    id="forgotPswd"
                    tabIndex={0}
                    href={resetPwdUrl}
                    target="_self"
                    style={{ cursor: "pointer" }}
                    onContextMenu={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.preventDefault();
                      redirectInSameTab(resetPwdUrl);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        redirectInSameTab(resetPwdUrl);
                      }
                    }}
                  >
                    {t("translations.LINK_93845637669062720_LABEL")}
                  </a>
                </p>
                {country === "cn" && (
                  <p className="loginForm__left__form__tip_tool">
                    <a
                      className="loginForm__left__form__tip_tool__trouble-signin"
                      id="forgotPswd"
                      href={`${process.env[`REACT_APP_SERVICE_URL${config.ENV_SUFIX}`]}/identity/mobile/signin`}
                      target="_self"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      onContextMenu={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.preventDefault();
                        redirectInSameTab(
                          `${process.env[`REACT_APP_SERVICE_URL${config.ENV_SUFIX}`]}/identity/mobile/signin`,
                        );
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          redirectInSameTab(
                            `${process.env[`REACT_APP_SERVICE_URL${config.ENV_SUFIX}`]}/identity/mobile/signin`,
                          );
                        }
                      }}
                    >
                      {t("translations.LINK_MOBILE_LABEL")}
                    </a>
                  </p>
                )}
              </div>
            </div>
          </div>
          <div
            className={`loginForm__right ${cipRu || hideCommerceSidePanel || isNotChinaDomain ? "cipRu-specific-class" : ""} ${hideCommerceSidePanel ? "loginForm__right--assistance-only" : ""}`}
          >
            {!isNotChinaDomain ? (
              <>
                {!cipRu && !hideCommerceSidePanel && (
                  <>
                    <p className="loginForm__right__signin-title">
                      {t("registerSideScreen.infoTitle1.title")}
                    </p>
                    <p className="loginForm__right__heading">
                      {t("registerSideScreen.infoTitle1.heading")}
                    </p>
                    {country !== "cn" && (
                      <ul>
                        <li>
                          <Trans
                            t={t}
                            i18nKey="registerSideScreen.infoTitle1.line1"
                          />
                        </li>
                        <li>{t("registerSideScreen.infoTitle1.line2")}</li>
                        <li>{t("registerSideScreen.infoTitle1.line3")}</li>
                        {(country === "us" || country === "ca") && (
                          <li>{t("registerSideScreen.infoTitle1.line4")}</li>
                        )}
                      </ul>
                    )}
                  </>
                )}
                {cipRu && !hideCommerceSidePanel && (
                  <div className="loginForm__right__divider">
                    <span className="loginForm__right__divider__line"></span>
                    <span className="loginForm__right__divider__text">
                      {t("registerSideScreen.infoTitle1.title")}
                    </span>
                    <span className="loginForm__right__divider__line"></span>
                  </div>
                )}

                {!hideCommerceSidePanel && (
                  <div className="loginForm__right__create-account">
                    <CoreButton
                      type="button"
                      variant="outline"
                      size="large"
                      href={createAccountUrl}
                      fullspace
                    >
                      <span slot="content">
                        {t("registerSideScreen.labels.createAcc")}
                      </span>
                    </CoreButton>
                  </div>
                )}
                {hideCommerceSidePanel && (
                  <p className="loginForm__right__tip_tool">
                    {t("needAssistance.needAssistance")}{" "}
                    <a
                      href={
                        rpdetailsUrl?.contactUsLink
                          ? rpdetailsUrl?.contactUsLink
                          : rpdetailsUrl?.siteUrl +
                            config.environment.pages.contact_us
                      }
                    >
                      {t("needAssistance.contactUs")}
                    </a>
                  </p>
                )}
                {!cipRu && !hideCommerceSidePanel && (
                  <p className="loginForm__right__tip_tool">
                    {t("registerSideScreen.labels.questions")}{" "}
                    <a
                      href={
                        rpdetailsUrl?.contactUsLink
                          ? rpdetailsUrl?.contactUsLink
                          : rpdetailsUrl?.siteUrl +
                            config.environment.pages.contact_us
                      }
                    >
                      {t("registerSideScreen.labels.contactUs")}
                    </a>
                  </p>
                )}
              </>
            ) : (
              <p className="loginForm__right__tip_tool">
                {hideCommerceSidePanel
                  ? t("needAssistance.needAssistance")
                  : t("registerSideScreen.labels.questions")}{" "}
                <a
                  href={
                    rpdetailsUrl?.contactUsLink
                      ? rpdetailsUrl?.contactUsLink
                      : rpdetailsUrl?.siteUrl +
                        config.environment.pages.contact_us
                  }
                >
                  {hideCommerceSidePanel
                    ? t("needAssistance.contactUs")
                    : t("registerSideScreen.labels.contactUs")}
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
      {/*       {footerHtml && <div dangerouslySetInnerHTML={{ __html: footerHtml }} />}
       */}
      {/* <Footer /> */}
    </Fragment>
  );
};

export default Signin;
